import React, { useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Table as TableIcon } from 'lucide-react';

type Props = { editor: any; max?: number; buttonClassName?: string };

const MAX = 8;

const TableGridPopover: React.FC<Props> = ({ editor, max = MAX, buttonClassName }) => {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<{ r: number; c: number } | null>(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Calculate panel position when opening
  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPanelPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX
      });
    }
  }, [open]);

  // close on outside click/esc
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!open) return;
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const grid = useMemo(
    () => Array.from({ length: max }, (_, r) =>
      Array.from({ length: max }, (_, c) => ({ r: r + 1, c: c + 1 }))
    ),
    [max]
  );

  const insert = (r: number, c: number) => {
    setOpen(false);
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: r, cols: c, withHeaderRow: true }).run();
  };

  const triggerClasses = `${buttonClassName ?? 'toolbar-btn'}${open ? ' toolbar-btn--active' : ''}`;

  return (
    <div className="relative rte-table-trigger">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Insert table"
        onClick={() => setOpen(v => !v)}
        className={triggerClasses}
      >
        <TableIcon size={16} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          className="rte-table-panel"
          style={{
            top: `${panelPosition.top}px`,
            left: `${panelPosition.left}px`,
            backgroundColor: 'var(--bg-elev)',
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            opacity: 1,
            isolation: 'isolate'
          }}
        >
          <div className="rte-table-grid" onMouseLeave={() => setHover(null)}>
            {grid.map((row, _) =>
              row.map(cell => {
                const active = hover && cell.r <= hover.r && cell.c <= hover.c;
                return (
                  <button
                    key={`${cell.r}-${cell.c}`}
                    type="button"
                    aria-label={`${cell.r} by ${cell.c} table`}
                    className={`rte-table-cell ${active ? 'is-active' : ''}`}
                    onMouseEnter={() => setHover({ r: cell.r, c: cell.c })}
                    onClick={() => insert(cell.r, cell.c)}
                  />
                );
              })
            )}
          </div>
          <div className="rte-table-label">
            {hover ? `${hover.r} × ${hover.c}` : `Insert table`}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default TableGridPopover;